import React, { useState, useEffect } from "react";
import axios from "axios";
import Layout from "../../layout/Layout";
import Header from "../../components/Header";
import CardComponent from "../../components/cards/CardComponent";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { TextfieldwithLabel } from "../../components/Buttons/Textfield";
import { SubmitButton } from "../../components/Buttons/Textfield";
import { fetchComboData } from "../../services/api";
import { getHeaders } from "../../utility/getHeader";
import DataTable from "../../components/data-tables/dataTable";
import fetchData from "../../functions/fetchData";

export default function TicketPricing() {
  const [categories, setCategories] = useState([]);
  const [data, setData] = useState([]);
  const [formdata, setFormData] = useState({
    category: "",
    itemName: "",
    price: ""
  });
  const [loading, setLoading] = useState(false);

  const columns = [
    { label: "ID", field: "id" },
    { label: "Category", field: "category_name" },
    { label: "Item Name", field: "item_name" },
    { label: "Price", field: "price" },
    { label: "Actions", field: "actions" }
  ];

  // Helper function to fetch and enrich ticket pricing data
  const fetchTicketPricingWithCategories = async () => {
    try {
      // Fetch ticket pricing data
      const ticketPricingData = await fetchData("ticket_pricing", null, "id", {});
      
      if (!ticketPricingData || !Array.isArray(ticketPricingData)) {
        console.warn("No ticket pricing data returned");
        setData([]);
        return;
      }
      
      // Fetch categories to map category_id to category_name
      const categoriesData = await fetchComboData("categories", "name");
      
      // Create a map of category_id to category_name
      const categoryMap = {};
      if (categoriesData && Array.isArray(categoriesData)) {
        categoriesData.forEach(cat => {
          categoryMap[cat.id] = cat.name;
        });
      }
      
      // Add category_name to each ticket pricing record
      const enrichedData = ticketPricingData.map(item => ({
        ...item,
        category_name: categoryMap[item.category_id] || 'Unknown'
      }));
      
      setData(enrichedData);
    } catch (error) {
      console.error("Error fetching ticket pricing data:", error);
      setData([]);
    }
  };

  useEffect(() => {
    // Fetch categories using fetchComboData
    const fetchCategories = async () => {
      try {
        const categoriesData = await fetchComboData("categories", "name");
        setCategories(categoriesData);
      } catch (error) {
        console.error("Error fetching categories:", error);
        setCategories([]);
      }
    };
    
    fetchCategories();
    fetchTicketPricingWithCategories();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(
        "/insertdata/ticket_pricing",
        {
          category_id: formdata.category,
          item_name: formdata.itemName,
          price: formdata.price
        },
        getHeaders()
      );
      
      // Fetch the updated data after successful submission
      await fetchTicketPricingWithCategories();
      
      toast.success("Ticket pricing saved successfully!");
      setFormData({
        category: "",
        itemName: "",
        price: ""
      });
    } catch (err) {
      toast.error("Error saving ticket pricing.");
      console.error(err.message);
    }
    setLoading(false);
  };

  // Handle delete success callback
  const handleDeleteSuccess = async () => {
    try {
      console.log("🔄 Refreshing data after deletion...");
      
      // Fetch ticket pricing data with category names
      const ticketPricingData = await fetchData("ticket_pricing", null, "id", {});
      const categoriesData = await fetchComboData("categories", "name");
      
      const categoryMap = {};
      categoriesData.forEach(cat => {
        categoryMap[cat.id] = cat.name;
      });
      
      const enrichedData = ticketPricingData.map(item => ({
        ...item,
        category_name: categoryMap[item.category_id] || 'Unknown'
      }));
      
      setData(enrichedData);
    } catch (error) {
      console.error("Error refreshing data after deletion:", error);
    }
  };

  return (
    <Layout>
      <Header title="Ticket Pricing" />
      <ToastContainer />
      <div className="row">
        <div className="col-lg-4 col-md-4 col-sm-12 col-xs-12">
          <CardComponent
            title="🎡 Add Amusement Park Ticket Pricing"
            headerColor="danger"
            pull="left"
            bodyClass="panel-body"
          >
            <div className="row">
              <div className="col-md-12">
                <form onSubmit={handleSubmit}>
                  <div className="panel panel-default card-view">
                    
                    <div className="form-group">
                      <label className="control-label col-md-3">Select Category</label>
                      <div className="col-md-9">
                        <select
                          name="category"
                          value={formdata.category}
                          onChange={handleInputChange}
                          required
                          className="form-control"
                        >
                          <option value="">-- Select Category --</option>
                          {categories.map(cat => (
                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <TextfieldwithLabel
                      id="itemName"
                      onChange={handleInputChange}
                      value={formdata.itemName}
                      type="text"
                      name="itemName"
                      lable="Item Name"
                    />

                    <TextfieldwithLabel
                      id="price"
                      onChange={handleInputChange}
                      value={formdata.price}
                      type="number"
                      name="price"
                      lable="Price"
                      step="0.01"
                      min="0"
                    />
                  </div>

                  <div className="form-group">
                    <label className="control-label mb-12"></label>
                    <SubmitButton
                      type="submit"
                      name={loading ? "Saving..." : "Save Ticket Pricing"}
                      cls="btn btn-darkblue btn-anim"
                      disabled={loading}
                    />
                  </div>
                </form>
              </div>
            </div>
          </CardComponent>
        </div>
        
        <div className="col-lg-8 col-md-8 col-sm-12 col-xs-12">
          {data.length === 0 ? (
            <p>No data available</p>
          ) : (
            <DataTable 
              columns={columns} 
              data={data} 
              tablename="ticket_pricing" 
              onDeleteSuccess={handleDeleteSuccess}
            />
          )}
        </div>
      </div>
    </Layout>
  );
}